import { ArrowLeftIcon, ArrowRightIcon } from '@asset-viz/internalized/icons'
import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import { ClockBearing3D } from '../decor/ClockBearing3D'
import { HtmlOverlay3D } from '../decor/HtmlOverlay3D'
import { Ruler3D } from '../decor/Ruler3D'
import { TankDecorGroup3D } from '../decor/TankDecorGroup3D'
import { FeatureDispatch } from '../features/FeatureDispatch'
import { PipeSupportFeature3D } from '../features/pipeSection/PipeSupportFeature3D'
import {
  planeGeometry,
  xWrappedShellGeometry,
} from '@asset-viz/utils/geometryDefines'
import {
  extractScale,
  HtmlOverlayPriority,
  Vec3,
} from '@asset-viz/utils/helpers'
import React from 'react'
import { Vector3 } from 'three'
import {
  GeckoCircumferentialWeld,
  GeckoHorizontalWeld,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoNozzle,
  GeckoPipeFlowDirection,
  GeckoPipeSection,
  GeckoPlate,
  GeckoVerticalWeld,
} from 'types'

import { ModelSurface3D } from './ModelSurface3D'
import { Surface3DGroupProps } from './Surface3D'

/**
 * A Rich Asset Model Pipe, plotted on a cartesian plane, with the center of its
 * base at "position" and decorated with Rich Asset features specific to pipes.
 * @param model               The pipe section component model
 * @param groupProps          Position, rotation, scale, and other 3D properties
 * @constructor
 */
export const PipeSurface3D: React.FC<
  { model: GeckoPipeSection } & Surface3DGroupProps
> = ({ model, ...groupProps }) => {
  const { show3D, showFeatureLabels } = useAssetVizInternal()
  const showFlowDirection = React.useMemo(
    () =>
      typeof showFeatureLabels === 'boolean'
        ? showFeatureLabels
        : showFeatureLabels({
            feature: 'flow_direction',
            componentModel: model,
          }),
    [model, showFeatureLabels]
  )

  const {
    bounds,
    scale,
    nozzles,
    verticalWelds,
    horizontalWelds,
    circumferentialWelds,
    pipeSeamWelds,
    plates,
    fixedSensors,
    pipeNonIntegralSupports,
    pipeIntegralSupports,
    pipeWraps,
  } = React.useMemo(() => {
    // TODO ENT-1322: add welds and plates back in
    const nozzles: Array<GeckoNozzle> = model.nozzlesArray
    const verticalWelds: Array<GeckoVerticalWeld> = model.verticalWeldsArray
    const horizontalWelds: Array<GeckoHorizontalWeld> =
      model.horizontalWeldsArray
    const circumferentialWelds: Array<GeckoCircumferentialWeld> =
      model.circumferentialWeldsArray
    const plates: Array<GeckoPlate> = model.platesArray
    const scale = extractScale(groupProps.scale).multiply(
      show3D
        ? new Vector3(
            model.radius.num,
            model.componentBounds.height.num,
            model.radius.num
          )
        : new Vector3(
            model.componentBounds.width.num,
            model.componentBounds.height.num,
            1.0
          )
    )

    return {
      bounds: model.componentBounds,
      scale: scale,
      nozzles: nozzles,
      verticalWelds: verticalWelds,
      horizontalWelds: horizontalWelds,
      circumferentialWelds: circumferentialWelds,
      plates: plates,
      fixedSensors: model.fixedSensorsArray,
      pipeNonIntegralSupports: model.pipeNonIntegralSupportsArray,
      pipeIntegralSupports: model.pipeIntegralSupportsArray,
      pipeSeamWelds: model.seamWeldsArray,
      pipeWraps: model.pipeWrapsArray,
    }
  }, [model, show3D, groupProps.scale])

  const geometry = React.useMemo(() => {
    if (!show3D) return planeGeometry
    return xWrappedShellGeometry
  }, [show3D])

  const positionXTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const relX = coord
        .convertTo(bounds.start.x.unit)
        .minus(bounds.start.x)
        .scale(1.0 / bounds.width.num).num
      if (!show3D) return [relX, 0, 0]
      const angle = 2.0 * Math.PI * (relX - 0.25) // rotated by 90 degrees in 3D mode
      return [Math.sin(angle), 0, Math.cos(angle)]
    },
    [show3D, bounds]
  )

  const positionYTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const relY = coord
        .convertTo(GeckoMeasurementUnit.Meters)
        .minus(bounds.start.y)
        .scale(1.0 / bounds.height.num).num
      if (!show3D) return [0, relY, 0]
      return [-0.875, relY, 0.5]
    },
    [show3D, bounds]
  )

  const positionClockBearing = React.useCallback<(coord: number) => Vector3>(
    (coord) => {
      const clockRange = 12
      const radianRange = 2 * Math.PI
      const clockRotation = model.clockBearingRotation ?? 0
      const coordToRadians =
        coord !== clockRange || clockRotation % 360 !== 0
          ? ((coord / clockRange) * radianRange + // divide by 12 since that's the range of the clock bearing
              (clockRotation * Math.PI) / 180) %
            radianRange
          : radianRange
      if (!show3D) return new Vector3(coordToRadians / (2 * Math.PI), 1, 0)
      const rotatedCoordToRadians = coordToRadians - Math.PI / 2 // rotated by 90 degrees in 3D mode
      const radialXPosition = Math.sin(rotatedCoordToRadians)
      const radialZPosition = Math.cos(rotatedCoordToRadians)
      return new Vector3(radialXPosition, 1.0, radialZPosition)
    },
    [show3D, model]
  )

  const flowDirectionPosition = React.useMemo(
    () =>
      show3D
        ? new Vector3(0.875 * 1.1, 0.5, 0.5 * 1.1)
        : new Vector3(1.0, 0.5, 0),
    [show3D]
  )
  return (
    <ModelSurface3D
      geometry={geometry}
      bounds={bounds}
      componentModel={model}
      {...groupProps}
      // Apply the true scaling last (override anything in groupProps)
      scale={scale}>
      <TankDecorGroup3D yPivot={show3D}>
        {/* Y ruler -------------------------------------------------------- */}
        <Ruler3D
          anchor={'bottom'}
          start={model.componentBounds.start.y}
          end={model.componentBounds.end.y}
          includeTicks
          includeStart
          includeEnd
          getTickPosition={positionYTick}
        />
        {model.flowDirection === undefined ? null : (
          <HtmlOverlay3D
            x={flowDirectionPosition.x}
            y={flowDirectionPosition.y}
            z={flowDirectionPosition.z}
            anchor={'top'}
            priority={HtmlOverlayPriority.MajorFeatureNamePlate}
            visible={showFlowDirection}>
            <div style={{ display: 'flex' }}>
              {model.flowDirection ===
              GeckoPipeFlowDirection.Positive ? null : (
                <ArrowLeftIcon
                  style={{ paddingRight: '1px', transform: 'translateY(15%)' }}
                />
              )}
              <small>Flow Direction</small>
              {model.flowDirection === GeckoPipeFlowDirection.Positive ? (
                <ArrowRightIcon
                  style={{ paddingLeft: '1px', transform: 'translateY(15%)' }}
                />
              ) : null}
            </div>
          </HtmlOverlay3D>
        )}
      </TankDecorGroup3D>
      {/* X ruler ---------------------------------------------------------- */}
      <TankDecorGroup3D>
        <Ruler3D
          anchor={'right'}
          start={model.componentBounds.start.x}
          end={model.componentBounds.end.x}
          includeTicks
          includeStart
          includeEnd={!show3D}
          getTickPosition={positionXTick}
        />
      </TankDecorGroup3D>
      <TankDecorGroup3D>
        {model.clockBearingRotation === undefined ? null : (
          <ClockBearing3D
            anchor={'left'}
            includeStart={!show3D && model.clockBearingRotation === 0}
            getTickPosition={positionClockBearing}
          />
        )}
      </TankDecorGroup3D>
      <FeatureDispatch
        features={[
          nozzles,
          verticalWelds,
          horizontalWelds,
          plates,
          fixedSensors,
          circumferentialWelds,
          pipeSeamWelds,
          pipeWraps,
        ]}
      />
      {/* Features which rely on extra data must be rendered manually */}
      {pipeNonIntegralSupports.map((pipeNonIntegralSupport) => (
        <PipeSupportFeature3D
          key={pipeNonIntegralSupport.id}
          model={pipeNonIntegralSupport}
          pipeRadius={model.radius}
        />
      ))}
      {pipeIntegralSupports.map((pipeIntegralSupport) => (
        <PipeSupportFeature3D
          key={pipeIntegralSupport.id}
          model={pipeIntegralSupport}
          pipeRadius={model.radius}
        />
      ))}
    </ModelSurface3D>
  )
}
